import {
  CopilotReviewEffortAvailability,
  CopilotReviewEffort,
  type CopilotReviewerRow,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'

export type CopilotReviewEffortValue = 'low' | 'medium' | 'high'

const COPILOT_REVIEW_EFFORT_BY_ENUM: Record<number, CopilotReviewEffortValue | undefined> = {
  [CopilotReviewEffort.UNSPECIFIED]: undefined,
  [CopilotReviewEffort.LOW]: 'low',
  [CopilotReviewEffort.MEDIUM]: 'medium',
  [CopilotReviewEffort.HIGH]: 'high',
}

const COPILOT_REVIEW_EFFORT_BY_NAME: Record<string, CopilotReviewEffortValue | undefined> = {
  COPILOT_REVIEW_EFFORT_UNSPECIFIED: undefined,
  COPILOT_REVIEW_EFFORT_LOW: 'low',
  COPILOT_REVIEW_EFFORT_MEDIUM: 'medium',
  COPILOT_REVIEW_EFFORT_HIGH: 'high',
  low: 'low',
  medium: 'medium',
  high: 'high',
}

export function normalizeCopilotReviewEffort(
  effort: CopilotReviewEffort | string | number | null | undefined,
): CopilotReviewEffortValue | undefined {
  if (effort == null || effort === '') return undefined
  if (typeof effort === 'number') return COPILOT_REVIEW_EFFORT_BY_ENUM[effort]
  return COPILOT_REVIEW_EFFORT_BY_NAME[effort]
}

const AVAILABILITY_BY_NAME: Record<string, CopilotReviewEffortAvailability> = {
  COPILOT_REVIEW_EFFORT_AVAILABILITY_UNSPECIFIED: CopilotReviewEffortAvailability.UNSPECIFIED,
  COPILOT_REVIEW_EFFORT_AVAILABILITY_AVAILABLE: CopilotReviewEffortAvailability.AVAILABLE,
  COPILOT_REVIEW_EFFORT_AVAILABILITY_COMING_SOON: CopilotReviewEffortAvailability.COMING_SOON,
}

function effortAvailability(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): CopilotReviewEffortAvailability {
  if (typeof availability === 'number') return availability
  if (typeof availability === 'string') {
    return AVAILABILITY_BY_NAME[availability] ?? CopilotReviewEffortAvailability.UNSPECIFIED
  }
  return CopilotReviewEffortAvailability.UNSPECIFIED
}

export type CopilotReviewEffortBadge = {label: string; variant: 'default'}

export function copilotReviewEffortAvailabilityBadge(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): CopilotReviewEffortBadge | undefined {
  return isCopilotReviewEffortComingSoon(availability) ? {label: 'Coming soon', variant: 'default'} : undefined
}

export function isCopilotReviewEffortComingSoon(
  availability: CopilotReviewEffortAvailability | string | number | null | undefined,
): boolean {
  return effortAvailability(availability) === CopilotReviewEffortAvailability.COMING_SOON
}

export function copilotReviewEffortLabel(
  row: CopilotReviewerRow,
  effort: CopilotReviewEffort | string | number | null | undefined,
): string | undefined {
  const copilotEffortValue = normalizeCopilotReviewEffort(effort)
  return copilotEffortValue
    ? row.reviewEffortOptions.find(option => normalizeCopilotReviewEffort(option.value) === copilotEffortValue)?.label
    : undefined
}

export function copilotReviewStatusText({
  row,
  requestedCopilotReviewEffort,
  lastCompletedCopilotReviewEffort,
}: {
  row: CopilotReviewerRow
  requestedCopilotReviewEffort: CopilotReviewEffort | string | number | null | undefined
  lastCompletedCopilotReviewEffort: CopilotReviewEffort | string | number | null | undefined
}): string | undefined {
  const requested = copilotReviewEffortLabel(row, requestedCopilotReviewEffort)?.toLowerCase()
  const completed = copilotReviewEffortLabel(row, lastCompletedCopilotReviewEffort)?.toLowerCase()

  if (requested && completed) return `Reviewing at ${requested} effort · Last completed: ${completed}`
  if (requested) return `Reviewing at ${requested} effort`
  if (completed) return `Reviewing · Last completed: ${completed}`
  if (row.reviewEffortOptions.length > 0) return 'Reviewing'
  return undefined
}
